// AI Actors' "Create a Talking Head" (issue 238): the actor goes to the Studio's Talking Head in
// the navigation's state, as live hands it over in its route state (`global-actor-card`,
// `useAvatarCardActions`, `usePresetFromRoute`). A reload applies it again, as live's: the
// browser keeps a navigation's state, and the set of taken ones starts empty.
import {
  useNavigate,
  useParams,
  type HistoryState,
  type ParsedLocation,
} from "@tanstack/react-router";

import type { AiActor } from "../../api/generated/model";
import { pickedOfActor, type PickedActor } from "./picked";

declare module "@tanstack/react-router" {
  interface HistoryState {
    /** The actor "Create a Talking Head" brings to the Studio's Talking Head. */
    talkingHeadActor?: PickedActor;
  }
}

/**
 * Opens Talking Head with the actor and its own voice: the Studio's video page, or the Pro Studio
 * redesign's Talking head page (spec ui-perf/pro-studio-redesign), which takes it the same way.
 */
export function useCreateTalkingHead(page: "studio" | "redesign" = "studio"): (actor: AiActor) => void {
  const navigate = useNavigate();
  const { teamId, brandId } = useParams({ strict: false });
  return (actor) => {
    if (teamId === undefined || brandId === undefined) return;
    const state = (previous: HistoryState) => ({ ...previous, talkingHeadActor: pickedOfActor(actor) });
    void (page === "redesign"
      ? navigate({ to: "/teams/$teamId/brands/$brandId/home2/create-talking", params: { teamId, brandId }, state })
      : navigate({
          to: "/teams/$teamId/brands/$brandId/generate",
          params: { teamId, brandId },
          search: { medium: "video" },
          state,
        }));
  };
}

/** The navigations whose actor the Studio already took (live's registry by location key). */
const taken = new Set<string>();

const keyOf = (location: ParsedLocation) => location.state.__TSR_key ?? location.state.key;

/** The actor this navigation brought, unless the Studio already took it. */
export function arrivingTalkingHeadActor(location: ParsedLocation): PickedActor | null {
  const actor = location.state.talkingHeadActor;
  const key = keyOf(location);
  if (actor === undefined || key === undefined || taken.has(key)) return null;
  return actor;
}

/**
 * The Studio took the navigation's actor: a composer opened again on the same navigation starts
 * without it, as live's preset applies once.
 */
export function tookTalkingHeadActor(location: ParsedLocation): void {
  const key = keyOf(location);
  if (key !== undefined) taken.add(key);
}
